import { useCallback } from 'react';
import * as Notifications from 'expo-notifications';
import { defineMessages, useIntl } from 'react-intl';

import { addMinutes, isPast, setMilliseconds } from 'date-fns';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { ignoreTimezone } from '@order/utils';

import {
  ORDER_FEEDBACK_NOTIFICATION_ANDROID_COLOR,
  ORDER_FEEDBACK_NOTIFICATIONS_CHANNEL_ID,
} from './useOrderFeedbackNotifications.constants';
import {
  checkIfOrderFeedbackNotificationsEnabled,
  getOrderFeedbackNotificationId,
} from './useOrderFeedbackNotifications.utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useOrderFeedbackNotifications = () => {
  const { formatMessage } = useIntl();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isOrderFeedbackNotificationsEnabled = useFeatureFlag('CELS-4251-order-feedback-notifications-enabled'); // prettier-ignore
  const orderFeedbackNotificationDelay = useFeatureFlag('CELS-4251-order-feedback-notification-delay'); // prettier-ignore

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Creates a local push notification (native only) to collect feedback from the customer,
   * after three hours of the order's wanted time.
   */
  const scheduleOrderFeedbackNotification = useCallback<ScheduleOrderFeedbackNotification>(
    async (props) => {
      if (!checkIfOrderFeedbackNotificationsEnabled()) return;

      const { orderId, wantedTime } = props;

      const orderWantedTime = ignoreTimezone(wantedTime);
      const hasValidWantedTime = Boolean(orderWantedTime && !isPast(orderWantedTime)); // prettier-ignore

      const shouldCreateNotification =
        orderWantedTime && isOrderFeedbackNotificationsEnabled && hasValidWantedTime;

      if (!shouldCreateNotification) return;

      const id = getOrderFeedbackNotificationId(orderId);

      const notificationDate = addMinutes(orderWantedTime, orderFeedbackNotificationDelay); // prettier-ignore
      const triggerDate = setMilliseconds(notificationDate, 0);

      const title = formatMessage(messages.orderFeedbackNotificationTitle);
      const body = formatMessage(messages.orderFeedbackNotificationBody);

      try {
        await Notifications.scheduleNotificationAsync({
          identifier: id,
          content: {
            title,
            body,
            data: { orderId },
            color: ORDER_FEEDBACK_NOTIFICATION_ANDROID_COLOR,
          },
          trigger: {
            type: Notifications.SchedulableTriggerInputTypes.DATE,
            date: triggerDate,
            channelId: ORDER_FEEDBACK_NOTIFICATIONS_CHANNEL_ID,
          },
        });
      } catch {}
    },
    [formatMessage, isOrderFeedbackNotificationsEnabled, orderFeedbackNotificationDelay],
  );

  /**
   * Cancels the local push notification for order feedback, based on the order ID (native only).
   */
  const cancelScheduledOrderFeedbackNotification = useCallback<CancelOrderFeedbackNotification>(
    async (orderId: string) => {
      if (!isOrderFeedbackNotificationsEnabled || !checkIfOrderFeedbackNotificationsEnabled())
        return;

      await Notifications.cancelScheduledNotificationAsync(getOrderFeedbackNotificationId(orderId));
    },
    [isOrderFeedbackNotificationsEnabled],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    scheduleOrderFeedbackNotification,
    cancelScheduledOrderFeedbackNotification,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  orderFeedbackNotificationTitle: {
    defaultMessage: "How'd that go?",
    description: 'Order Feedback Notification | Title',
  },
  orderFeedbackNotificationBody: {
    defaultMessage: 'Rate your order so we can do even better next time.',
    description: 'Order Feedback Notification | Body',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScheduleOrderFeedbackNotification = (
  input: Readonly<{
    orderId: string;
    wantedTime: string;
  }>,
) => Promise<void>;

export type CancelOrderFeedbackNotification = (orderId: string) => Promise<void>;
